import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Grid, TextField, MenuItem, FormControlLabel, Checkbox, Button, Snackbar, Alert, Paper, CircularProgress } from "@mui/material";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { getData, postData } from "../../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../../services/endpoints";
import { useSearchParams } from 'react-router-dom';

const terminationChecklist = [
    { key: 'exitInterview', label: 'Exit interview scheduled' },
    { key: 'assetsCollected', label: 'Company assets collected (Laptop, Phone, ID)' },
    { key: 'finalPayroll', label: 'Final payroll processed' },
    { key: 'accessRevoked', label: 'Access permissions revoked' },
    { key: 'terminationProcessCompleted', label: 'All Check are completed. Complete the Termination Process' }
];

const getPayrollMonthsOptions = () => {
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const financialYearEndMonth = 2;
    const financialYearEndYear = currentMonth <= financialYearEndMonth ? currentYear : currentYear + 1;

    const options = [];
    let year = currentYear;
    let month = currentMonth;

    while (year < financialYearEndYear || (year === financialYearEndYear && month <= financialYearEndMonth)) {
        options.push(`${months[month]} ${year}`);
        month++;
        if (month > 11) {
            month = 0;
            year++;
        }
    }

    let prevMonthDate = new Date(currentDate);
    prevMonthDate.setMonth(prevMonthDate.getMonth() - 1);
    const prevMonthOption = `${months[prevMonthDate.getMonth()]} ${prevMonthDate.getFullYear()}`;

    return [prevMonthOption, ...options];
};

function EmployeeExitDetails({ onDataLoaded, onExitSaved, showEntryFields = false }) {
    const [searchParams] = useSearchParams();
    const empIdFromUrl = searchParams.get("employeeId");
    const [terminationProcessStatus, setTerminationProcessStatus] = useState('');
    const [finalExitData] = useState(null);
    const payrollMonthsOptions = getPayrollMonthsOptions();
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [existingExitData, setExistingExitData] = useState(null);

    const [exitDetails, setExitDetails] = useState({
        type: 'Resignation',
        date_of_notice: null,
        final_date_of_exit: null,
        notice_period_days: '',
        reason_for_exit: '',
    });

    const [checklistData, setChecklistData] = useState({
        exitInterview: {
            exit_interview_scheduled: false,
            interview_date: ''
        },
        assetsCollected: {
            assets_collected: false,
            assets_details: ''
        },
        finalPayroll: {
            final_payroll_processed: false,
            payroll_month: ''
        },
        accessRevoked: {
            access_revoked: false,
            revocation_details: ''
        },
        terminationProcessCompleted: false
    });

    const handleExitChange = (event) => {
        const { name, value } = event.target;
        setExitDetails(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleDateChange = (name, newValue) => {
        setExitDetails(prev => ({
            ...prev,
            [name]: newValue,
        }));
    };

    const handleChecklistChange = (key, event) => {
        const isChecked = event.target.checked;

        if (key === 'terminationProcessCompleted') {
            setChecklistData(prev => ({
                ...prev,
                [key]: isChecked
            }));

            if (isChecked) {
                setTerminationProcessStatus('Completed');
            } else {
                setTerminationProcessStatus(finalExitData?.status === 'Completed' ? 'Initiate' : finalExitData?.status || 'Initiate');
            }
        } else {
            setChecklistData(prev => ({
                ...prev,
                [key]: {
                    ...prev[key],
                    [`${key === 'exitInterview' ? 'exit_interview_scheduled' :
                        key === 'assetsCollected' ? 'assets_collected' :
                            key === 'finalPayroll' ? 'final_payroll_processed' :
                                'access_revoked'}`]: isChecked,

                    ...(key === 'exitInterview' && { interview_date: isChecked ? prev[key].interview_date : '' }),
                    ...(key === 'finalPayroll' && { payroll_month: isChecked ? prev[key].payroll_month : '' }),
                    ...(key === 'accessRevoked' && { revocation_details: isChecked ? prev[key].revocation_details : '' }),
                    ...(key === 'assetsCollected' && { assets_details: isChecked ? prev[key].assets_details : '' }),
                },
            }));
        }
    };

    const handleConditionalChecklistChange = (key, name, value) => {
        setChecklistData(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                [name]: value,
            },
        }));
    };

    const fetchExitRehireDetails = useCallback(async () => {
        if (!empIdFromUrl) return;
        try {
            setLoading(true);
            const response = await getData(API_ENDPOINTS.getEmployeeExitAndRehireDetails(empIdFromUrl));
            const activeExit = response.status === "SUCCESS" &&
                response.result?.exitDetails &&
                Array.isArray(response.result.exitDetails) &&
                response.result.exitDetails.length > 0;

            const latestExit = activeExit ? response.result.exitDetails[0] : null;
            const rehireCompleted = Boolean(
                response.result?.personalDetails?.rehire_date ||
                response.result?.personalDetails?.rehire_reason ||
                latestExit?.rehire_date ||
                latestExit?.rehire_reason
            );

            if (activeExit) {
                setExistingExitData(latestExit);
                if (onDataLoaded) onDataLoaded(!rehireCompleted);
            } else {
                setExistingExitData(null);
                if (onDataLoaded) onDataLoaded(false);
            }
        } catch (error) {
            console.error("Error fetching details:", error);
            setExistingExitData(null);
        } finally {
            setLoading(false);
        }
    }, [empIdFromUrl, onDataLoaded]);

    useEffect(() => {
        fetchExitRehireDetails();
    }, [fetchExitRehireDetails]);

    const handleSubmit = async () => {
        if (!exitDetails.type) {
            setSnackbar({ open: true, message: "Exit type is mandatory.", severity: 'error' });
            return;
        }

        if (!empIdFromUrl) {
            setSnackbar({ open: true, message: "User ID not found. Please log in again.", severity: 'error' });
            return;
        }

        setLoading(true);

        const status = checklistData.terminationProcessCompleted ? 'Completed' : 'Initiate';

        const buildConditionalPayload = (key, boolKey, dataKeys) => {
            if (checklistData[key][boolKey]) {
                const data = { [boolKey]: true };
                dataKeys.forEach(dk => {
                    const value = checklistData[key][dk];
                    if (value) {
                        data[dk] = value;
                    } else if (dk.includes('date') || dk.includes('month')) {
                        data[dk] = null;
                    }
                });
                return data;
            }
            return {
                [boolKey]: false,
                ...dataKeys.reduce((acc, dk) => ({ ...acc, [dk]: null }), {})
            };
        };

        const apiPayload = {
            user_id: empIdFromUrl,
            type: exitDetails.type,
            // Warning fixed: Used 'dayjs' to format dates for API consumption
            date_of_notice: exitDetails.date_of_notice ? dayjs(exitDetails.date_of_notice).format('YYYY-MM-DD') : null,
            final_date_of_exit: exitDetails.final_date_of_exit ? dayjs(exitDetails.final_date_of_exit).format('YYYY-MM-DD') : null,
            notice_period_days: exitDetails.notice_period_days ? parseInt(exitDetails.notice_period_days) : 0,
            reason_for_exit: exitDetails.reason_for_exit,

            ...buildConditionalPayload('exitInterview', 'exit_interview_scheduled', ['interview_date']),
            ...buildConditionalPayload('assetsCollected', 'assets_collected', ['assets_details']),
            ...buildConditionalPayload('finalPayroll', 'final_payroll_processed', ['payroll_month']),
            ...buildConditionalPayload('accessRevoked', 'access_revoked', ['revocation_details']),

            exit_status: status
        };

        try {
            const response = await postData(API_ENDPOINTS.createEmployeeExit, apiPayload);
            if (response.status === 'SUCCESS') {
                setSnackbar({
                    open: true,
                    message: `Exit Process ${status === 'Completed' ? 'Completed' : terminationProcessStatus ? 'Updated' : 'Initiated'} successfully!`,
                    severity: 'success'
                });
                await fetchExitRehireDetails();
                if (onExitSaved) {
                    await onExitSaved();
                }
            } else {
                setSnackbar({
                    open: true,
                    message: response.data?.message?.msg || 'Failed to save exit process',
                    severity: 'error'
                });
            }
        } catch (error) {
            console.log('Error while sending the Exit Details', error);
            setSnackbar({ open: true, message: "Something went wrong.", severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : (!existingExitData || showEntryFields) ? (
                    <>
                        <Typography variant="h6" fontWeight={600} component="h2" gutterBottom>
                            1. Termination or Resignation Details
                        </Typography>
                        <Grid container spacing={2} mt={2}>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <TextField
                                    select
                                    fullWidth
                                    size='small'
                                    label="Type"
                                    name="type"
                                    value={exitDetails.type}
                                    onChange={handleExitChange}
                                    required
                                >
                                    <MenuItem value="Resignation">Resignation</MenuItem>
                                    <MenuItem value="Termination">Termination</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <DatePicker
                                    label="Date of Notice"
                                    value={exitDetails.date_of_notice}
                                    format="DD/MM/YYYY"
                                    onChange={(newValue) => handleDateChange('date_of_notice', newValue)}
                                    slotProps={{ textField: { size: 'small' } }}
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <DatePicker
                                    label="Final Date of Exit"
                                    value={exitDetails.final_date_of_exit}
                                    format="DD/MM/YYYY"
                                    onChange={(newValue) => handleDateChange('final_date_of_exit', newValue)}
                                    slotProps={{ textField: { size: 'small' } }}
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <TextField
                                    fullWidth
                                    size='small'
                                    label="Notice Period (Days)"
                                    type="number"
                                    name="notice_period_days"
                                    value={exitDetails.notice_period_days}
                                    onChange={handleExitChange}
                                    helperText="Actual notice period served."
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="Reason for Exit"
                                    name="reason_for_exit"
                                    value={exitDetails.reason_for_exit}
                                    onChange={handleExitChange}
                                    multiline
                                    rows={3}
                                />
                            </Grid>
                        </Grid>

                        <Typography variant="h6" fontWeight={600} component="h2" gutterBottom mt={3}>
                            2. Termination Process Checklist
                        </Typography>
                        <Grid container spacing={2} mt={2}>
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={checklistData.exitInterview.exit_interview_scheduled}
                                                onChange={(e) => handleChecklistChange('exitInterview', e)}
                                            />
                                        }
                                        label={terminationChecklist.find(i => i.key === 'exitInterview').label}
                                    />
                                    {checklistData.exitInterview.exit_interview_scheduled && (
                                        <DatePicker
                                            label="Interview Date"
                                            format="DD/MM/YYYY"
                                            value={checklistData.exitInterview.interview_date ? dayjs(checklistData.exitInterview.interview_date) : null}
                                            onChange={(newValue) =>
                                                handleConditionalChecklistChange(
                                                    'exitInterview',
                                                    'interview_date',
                                                    newValue ? newValue.format('YYYY-MM-DD') : ''
                                                )
                                            }
                                            slotProps={{
                                                textField: {
                                                    size: 'small'
                                                }
                                            }}
                                            sx={{ width: 200 }}
                                        />
                                    )}
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size='medium'
                                                checked={checklistData.assetsCollected.assets_collected}
                                                onChange={(e) => handleChecklistChange('assetsCollected', e)}
                                            />
                                        }
                                        label={terminationChecklist.find(i => i.key === 'assetsCollected').label}
                                    />
                                    {checklistData.assetsCollected.assets_collected && (
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Assets Details"
                                            size="small"
                                            name="assets_details"
                                            value={checklistData.assetsCollected.assets_details}
                                            onChange={(e) => handleConditionalChecklistChange('assetsCollected', 'assets_details', e.target.value)}
                                            sx={{ minWidth: 400 }}
                                        />
                                    )}
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={checklistData.finalPayroll.final_payroll_processed}
                                                onChange={(e) => handleChecklistChange('finalPayroll', e)}
                                            />
                                        }
                                        label={terminationChecklist.find(i => i.key === 'finalPayroll').label}
                                    />
                                    {checklistData.finalPayroll.final_payroll_processed && (
                                        <TextField
                                            select
                                            label="Payroll Month"
                                            size="small"
                                            name="payroll_month"
                                            value={checklistData.finalPayroll.payroll_month}
                                            onChange={(e) => handleConditionalChecklistChange('finalPayroll', 'payroll_month', e.target.value)}
                                            sx={{ width: 250 }}
                                        >
                                            {payrollMonthsOptions.map(month => (
                                                <MenuItem key={month} value={month}>{month}</MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={checklistData.accessRevoked.access_revoked}
                                                onChange={(e) => handleChecklistChange('accessRevoked', e)}
                                            />
                                        }
                                        label={terminationChecklist.find(i => i.key === 'accessRevoked').label}
                                    />
                                    {checklistData.accessRevoked.access_revoked && (
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Revocation Details"
                                            size="small"
                                            name="revocation_details"
                                            value={checklistData.accessRevoked.revocation_details}
                                            onChange={(e) => handleConditionalChecklistChange('accessRevoked', 'revocation_details', e.target.value)}
                                            sx={{ minWidth: 400 }}
                                        />
                                    )}
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={checklistData.terminationProcessCompleted}
                                            onChange={(e) => handleChecklistChange('terminationProcessCompleted', e)}
                                        />
                                    }
                                    label={terminationChecklist.find(i => i.key === 'terminationProcessCompleted').label}
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                disabled={loading}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                {loading ? 'Saving...' :
                                    (finalExitData?.status === 'Completed' || checklistData.terminationProcessCompleted)
                                        ? 'Complete Exit Process'
                                        : terminationProcessStatus === 'Initiate'
                                            ? 'Save Exit Details'
                                            : 'Initiate Exit Process'
                                }
                            </Button>
                        </Box>
                    </>
                ) : (
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, backgroundColor: '#f9f9f9' }}>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mb: 2, color: 'primary.main' }}>
                            Termination or Resignation Details
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 600, textTransform: 'uppercase' }}>
                                    Type
                                </Typography>
                                <Typography variant="body1" fontWeight={500}>
                                    {existingExitData?.type || "—"}
                                </Typography>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 600, textTransform: 'uppercase' }}>
                                    Date of Notice
                                </Typography>
                                <Typography variant="body1" fontWeight={500}>
                                    {existingExitData?.date_of_notice ? dayjs(existingExitData.date_of_notice).format('DD MMM YYYY') : "—"}
                                </Typography>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 600, textTransform: 'uppercase' }}>
                                    Final Date of Exit
                                </Typography>
                                <Typography variant="body1" fontWeight={500}>
                                    {existingExitData?.final_date_of_exit ? dayjs(existingExitData.final_date_of_exit).format('DD MMM YYYY') : "—"}
                                </Typography>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 600, textTransform: 'uppercase' }}>
                                    Notice Period
                                </Typography>
                                <Typography variant="body1" fontWeight={500}>
                                    {existingExitData?.notice_period_days} Days
                                </Typography>
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
                                        Reason for Exit
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {existingExitData?.reason_for_exit || "No reason provided."}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                )}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
                </Snackbar>
            </Box>
        </LocalizationProvider>
    );
}

export default EmployeeExitDetails;